import React, { useState, useEffect } from 'react';
import api, { extractApiError } from '../../services/api';
import toast from 'react-hot-toast';

const CARRIER_ENDPOINTS = {
  correios: '/correios/admin/etiqueta',
  jadlog: '/jadlog/admin/etiqueta',
  loggi: '/loggi/admin/etiqueta',
  'jt-express': '/jt-express/admin/etiqueta',
};

const PESSOA_INICIAL = {
  nome: '', documento: '', telefone: '', email: '',
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
};

const INITIAL_FORM = {
  remetente: { ...PESSOA_INICIAL },
  destinatario: { ...PESSOA_INICIAL },
  peso: '', comprimento: '', largura: '', altura: '',
  vlDeclarado: '', conteudo: 'Mercadoria',
};

export default function LabelPage() {
  const [carriers, setCarriers] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [resultado, setResultado] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [errors, setErrors] = useState({});
  const [integrationOpen, setIntegrationOpen] = useState(false);

  useEffect(() => {
    api.get('/freight/carriers/enabled')
      .then(({ data }) => {
        const list = data.data?.carriers || data.data || [];
        setCarriers(list);
        if (list.length > 0) setSelectedSlug(list[0].slug);
      })
      .catch(() => toast.error('Erro ao carregar transportadoras'))
      .finally(() => setLoadingCarriers(false));
  }, []);

  const selected = carriers.find((c) => c.slug === selectedSlug);
  const endpoint = selectedSlug ? CARRIER_ENDPOINTS[selectedSlug] : null;

  function setPessoa(tipo, field, value) {
    setForm((f) => ({ ...f, [tipo]: { ...f[tipo], [field]: value } }));
    setErrors((e) => ({ ...e, [`${tipo}.${field}`]: undefined }));
  }
  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validatePessoa(pessoa, prefix, errs) {
    if (!pessoa.nome.trim()) errs[`${prefix}.nome`] = 'Nome obrigatório';
    if (!pessoa.documento.trim()) errs[`${prefix}.documento`] = 'CPF/CNPJ obrigatório';
    if (!/^\d{5}-?\d{3}$/.test(pessoa.cep)) errs[`${prefix}.cep`] = 'CEP inválido';
    if (!pessoa.logradouro.trim()) errs[`${prefix}.logradouro`] = 'Logradouro obrigatório';
    if (!pessoa.cidade.trim()) errs[`${prefix}.cidade`] = 'Cidade obrigatória';
    if (!pessoa.uf.trim()) errs[`${prefix}.uf`] = 'UF obrigatória';
  }

  function validate() {
    const errs = {};
    validatePessoa(form.remetente, 'remetente', errs);
    validatePessoa(form.destinatario, 'destinatario', errs);
    if (!form.peso || Number(form.peso) <= 0) errs.peso = 'Peso obrigatório (gramas)';
    if (!form.comprimento || Number(form.comprimento) <= 0) errs.comprimento = 'Obrigatório (cm)';
    if (!form.largura || Number(form.largura) <= 0) errs.largura = 'Obrigatório (cm)';
    if (!form.altura || Number(form.altura) <= 0) errs.altura = 'Obrigatório (cm)';
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!endpoint) return;
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); toast.error('Preencha os campos obrigatórios'); return; }

    const requestBody = {
      remetente: form.remetente,
      destinatario: form.destinatario,
      peso: Number(form.peso),
      comprimento: Number(form.comprimento),
      largura: Number(form.largura),
      altura: Number(form.altura),
      vlDeclarado: form.vlDeclarado ? Number(form.vlDeclarado) : 0,
      conteudo: form.conteudo,
    };

    setLoading(true);
    setResultado(null);
    setDebugInfo(null);

    try {
      const { data } = await api.post(endpoint, requestBody);
      setResultado(data.resultado || data);
      setDebugInfo({ request: requestBody, response: data, isError: !(data.resultado?.sucesso ?? data.sucesso) });
      if (data.resultado?.sucesso ?? data.sucesso) toast.success('Etiqueta emitida com sucesso!');
      else toast.error(data.resultado?.erro || 'Falha ao emitir etiqueta');
    } catch (err) {
      const msg = extractApiError(err, 'Erro ao emitir etiqueta');
      const errData = err.response?.data || { error: msg };
      setResultado(errData.resultado || null);
      setDebugInfo({ request: requestBody, response: errData, isError: true });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  function downloadEtiqueta() {
    if (!resultado?.etiquetaBase64) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${resultado.etiquetaBase64}`;
    link.download = `etiqueta-${resultado.codigoRastreio || resultado.numeroPedido || 'envio'}.pdf`;
    link.click();
  }

  return (
    <div>
      <div style={s.pageHeader}>
        <h1 style={s.pageTitle}>Emissão de Etiquetas</h1>
        <p style={s.pageDesc}>Solicite e emita etiquetas de envio nas transportadoras ativas</p>
      </div>

      <div style={s.carrierBar}>
        {loadingCarriers ? (
          <span style={s.carrierLoading}>Carregando transportadoras...</span>
        ) : carriers.length === 0 ? (
          <span style={s.carrierLoading}>Nenhuma transportadora habilitada</span>
        ) : (
          carriers.map((c) => (
            <button
              key={c.slug}
              onClick={() => { setSelectedSlug(c.slug); setResultado(null); setDebugInfo(null); }}
              style={{ ...s.carrierBtn, ...(selectedSlug === c.slug ? s.carrierBtnActive : {}) }}
            >
              {c.name}
              {!CARRIER_ENDPOINTS[c.slug] && <span style={s.carrierTag}>em breve</span>}
            </button>
          ))
        )}
      </div>

      {selected && !endpoint && (
        <div style={s.notImpl}>
          <span style={{ fontSize: '24px' }}>🚧</span>
          <div>
            <strong>Emissão não disponível para {selected.name}</strong>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#92400e' }}>
              A emissão de etiqueta para esta transportadora ainda não foi implementada.
            </p>
          </div>
        </div>
      )}

      {endpoint && (
        <div style={s.layout}>
          <div style={s.formCard}>
            <h2 style={s.cardTitle}>📤 Remetente</h2>
            <PessoaForm pessoa={form.remetente} prefix="remetente" set={setPessoa} errors={errors} />

            <h2 style={{ ...s.cardTitle, marginTop: '24px' }}>📥 Destinatário</h2>
            <PessoaForm pessoa={form.destinatario} prefix="destinatario" set={setPessoa} errors={errors} />

            <h2 style={{ ...s.cardTitle, marginTop: '24px' }}>📦 Pacote</h2>
            <form onSubmit={handleSubmit} noValidate style={s.form}>
              <Field label="Conteúdo">
                <input style={inp()} value={form.conteudo} onChange={(e) => set('conteudo', e.target.value)} placeholder="Mercadoria" />
              </Field>
              <Field label="Peso (gramas)" error={errors.peso}>
                <input style={inp(errors.peso)} type="number" min="1" value={form.peso} onChange={(e) => set('peso', e.target.value)} placeholder="ex: 500" />
              </Field>
              <div style={s.row3}>
                <Field label="Comprimento (cm)" error={errors.comprimento}>
                  <input style={inp(errors.comprimento)} type="number" min="1" value={form.comprimento} onChange={(e) => set('comprimento', e.target.value)} placeholder="20" />
                </Field>
                <Field label="Largura (cm)" error={errors.largura}>
                  <input style={inp(errors.largura)} type="number" min="1" value={form.largura} onChange={(e) => set('largura', e.target.value)} placeholder="15" />
                </Field>
                <Field label="Altura (cm)" error={errors.altura}>
                  <input style={inp(errors.altura)} type="number" min="1" value={form.altura} onChange={(e) => set('altura', e.target.value)} placeholder="10" />
                </Field>
              </div>
              <Field label="Valor Declarado (R$) — opcional">
                <input style={inp()} type="number" min="0" step="0.01" value={form.vlDeclarado} onChange={(e) => set('vlDeclarado', e.target.value)} placeholder="0,00" />
              </Field>

              <button type="submit" disabled={loading} style={s.btnSubmit}>
                {loading ? '⏳ Emitindo...' : `🏷️ Emitir Etiqueta — ${selected?.name}`}
              </button>
            </form>
          </div>

          <div style={s.resultsPanel}>
            <h2 style={s.cardTitle}>📋 Resultado</h2>

            {!resultado && !loading && !debugInfo && (
              <div style={s.emptyState}>
                <span style={s.emptyIcon}>🏷️</span>
                <p>Preencha os dados e clique em "Emitir Etiqueta" para gerar o envio.</p>
              </div>
            )}

            {loading && (
              <div style={s.emptyState}>
                <span style={s.emptyIcon}>⏳</span>
                <p>Emitindo etiqueta com {selected?.name}...</p>
              </div>
            )}

            {resultado && !loading && (
              resultado.sucesso ? (
                <div style={s.successBox}>
                  <div style={s.successTitle}>✅ Etiqueta emitida com sucesso</div>
                  <div style={s.resultRow}><span style={s.resultLabel}>Código de rastreio</span><span style={s.resultVal}>{resultado.codigoRastreio || '—'}</span></div>
                  <div style={s.resultRow}><span style={s.resultLabel}>Número do pedido</span><span style={s.resultVal}>{resultado.numeroPedido || '—'}</span></div>
                  {resultado.etiquetaBase64 && (
                    <button style={s.btnDownload} onClick={downloadEtiqueta}>⬇️ Baixar Etiqueta (PDF)</button>
                  )}
                  {resultado.etiquetaUrl && (
                    <a href={resultado.etiquetaUrl} target="_blank" rel="noreferrer" style={s.btnDownload}>⬇️ Baixar Etiqueta (PDF)</a>
                  )}
                  {!resultado.etiquetaBase64 && !resultado.etiquetaUrl && (
                    <div style={s.warnBox}>⚠️ Pedido criado, mas a etiqueta em PDF não está disponível para esta transportadora/situação. {resultado.erro}</div>
                  )}
                </div>
              ) : (
                <div style={s.errorBanner}>
                  <div style={s.errorBannerTitle}>❌ Falha na emissão</div>
                  <div style={s.errorBannerMsg}>{resultado.erro || 'Erro desconhecido'}</div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {debugInfo && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={s.debugCard}>
            <button style={s.debugToggle} onClick={() => setIntegrationOpen((o) => !o)}>
              <span style={s.debugEndpoint}>🔧 Detalhes da Integração</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ ...s.debugStatus, color: debugInfo.isError ? '#f87171' : '#4ade80' }}>
                  {debugInfo.isError ? '● Erro' : '● Sucesso'}
                </span>
                <span style={s.debugChevron}>{integrationOpen ? '▲' : '▼'}</span>
              </div>
            </button>

            {integrationOpen && (
              <>
                <div style={s.debugPanels}>
                  <div style={s.debugPanel}>
                    <div style={s.debugPanelTitle}>REQUEST — body enviado</div>
                    <pre style={s.debugJson}>{JSON.stringify(debugInfo.request, null, 2)}</pre>
                  </div>
                  <div style={s.debugPanel}>
                    <div style={s.debugPanelTitle}>RESPONSE — retorno da API</div>
                    <pre style={s.debugJson}>{JSON.stringify(debugInfo.response, null, 2)}</pre>
                  </div>
                </div>

                {debugInfo.response?.debug?.credentials && (
                  <div style={s.debugSection}>
                    <div style={s.debugPanelTitle}>CREDENCIAIS UTILIZADAS</div>
                    <div style={s.credGrid}>
                      {Object.entries(debugInfo.response.debug.credentials).map(([k, v]) => (
                        <div key={k} style={s.credRow}>
                          <span style={s.credKey}>{k}</span>
                          <span style={s.credVal}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {debugInfo.response?.debug?.backendLogs?.length > 0 && (
                  <div style={s.debugSection}>
                    <div style={s.debugPanelTitle}>LOGS DO BACKEND</div>
                    <pre style={s.debugJson}>
                      {debugInfo.response.debug.backendLogs.map((l) => `[${l.timestamp}] ${l.level.toUpperCase()}: ${l.message}`).join('\n')}
                    </pre>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PessoaForm({ pessoa, prefix, set, errors }) {
  return (
    <div style={s.form}>
      <div style={s.row2}>
        <Field label="Nome / Razão Social" error={errors[`${prefix}.nome`]}>
          <input style={inp(errors[`${prefix}.nome`])} value={pessoa.nome} onChange={(e) => set(prefix, 'nome', e.target.value)} placeholder="Nome completo" />
        </Field>
        <Field label="CPF / CNPJ" error={errors[`${prefix}.documento`]}>
          <input style={inp(errors[`${prefix}.documento`])} value={pessoa.documento} onChange={(e) => set(prefix, 'documento', e.target.value)} placeholder="000.000.000-00" />
        </Field>
      </div>
      <div style={s.row2}>
        <Field label="Telefone">
          <input style={inp()} value={pessoa.telefone} onChange={(e) => set(prefix, 'telefone', e.target.value)} placeholder="(11) 99999-9999" />
        </Field>
        <Field label="E-mail">
          <input style={inp()} type="email" value={pessoa.email} onChange={(e) => set(prefix, 'email', e.target.value)} placeholder="email@exemplo.com" />
        </Field>
      </div>
      <div style={s.row2}>
        <Field label="CEP" error={errors[`${prefix}.cep`]}>
          <input style={inp(errors[`${prefix}.cep`])} value={pessoa.cep} onChange={(e) => set(prefix, 'cep', e.target.value)} placeholder="00000-000" maxLength={9} />
        </Field>
        <Field label="UF" error={errors[`${prefix}.uf`]}>
          <input style={inp(errors[`${prefix}.uf`])} value={pessoa.uf} onChange={(e) => set(prefix, 'uf', e.target.value.toUpperCase())} placeholder="SP" maxLength={2} />
        </Field>
      </div>
      <Field label="Logradouro" error={errors[`${prefix}.logradouro`]}>
        <input style={inp(errors[`${prefix}.logradouro`])} value={pessoa.logradouro} onChange={(e) => set(prefix, 'logradouro', e.target.value)} placeholder="Rua, Avenida..." />
      </Field>
      <div style={s.row3}>
        <Field label="Número">
          <input style={inp()} value={pessoa.numero} onChange={(e) => set(prefix, 'numero', e.target.value)} placeholder="123" />
        </Field>
        <Field label="Complemento">
          <input style={inp()} value={pessoa.complemento} onChange={(e) => set(prefix, 'complemento', e.target.value)} placeholder="Apto, Bloco..." />
        </Field>
        <Field label="Bairro">
          <input style={inp()} value={pessoa.bairro} onChange={(e) => set(prefix, 'bairro', e.target.value)} placeholder="Bairro" />
        </Field>
      </div>
      <Field label="Cidade" error={errors[`${prefix}.cidade`]}>
        <input style={inp(errors[`${prefix}.cidade`])} value={pessoa.cidade} onChange={(e) => set(prefix, 'cidade', e.target.value)} placeholder="Cidade" />
      </Field>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <label style={s.label}>{label}</label>
      {children}
      {error && <span style={{ fontSize: '12px', color: '#dc2626' }}>{error}</span>}
    </div>
  );
}

function inp(error) {
  return {
    padding: '9px 12px',
    border: `1.5px solid ${error ? '#dc2626' : '#e5e7eb'}`,
    borderRadius: '7px',
    fontSize: '14px',
    background: error ? '#fef2f2' : '#f9fafb',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };
}

const s = {
  pageHeader: { marginBottom: '20px' },
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageDesc: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },

  carrierBar: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  carrierLoading: { fontSize: '14px', color: '#9ca3af' },
  carrierBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '8px',
    border: '1.5px solid #e5e7eb', background: '#fff',
    fontSize: '14px', fontWeight: '500', color: '#374151',
    cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
  },
  carrierBtnActive: { background: '#eff6ff', borderColor: '#2563eb', color: '#1e40af' },
  carrierTag: {
    fontSize: '10px', background: '#fef3c7', color: '#92400e',
    padding: '1px 6px', borderRadius: '8px', fontWeight: '600',
  },

  notImpl: {
    display: 'flex', alignItems: 'flex-start', gap: '14px',
    background: '#fffbeb', border: '1.5px solid #fde68a',
    borderRadius: '10px', padding: '16px 20px', marginBottom: '20px',
    color: '#92400e',
  },

  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(380px, 480px) 1fr',
    gap: '20px', alignItems: 'start', marginBottom: '20px',
  },
  formCard: {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
  },
  resultsPanel: {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6', minHeight: '300px',
  },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '20px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
  label: { fontSize: '13px', fontWeight: '500', color: '#374151' },
  btnSubmit: {
    padding: '11px', background: 'linear-gradient(135deg, #1e40af, #2563eb)',
    color: '#fff', border: 'none', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    marginTop: '4px', fontFamily: 'inherit',
  },

  emptyState: { textAlign: 'center', padding: '48px 24px', color: '#9ca3af' },
  emptyIcon: { fontSize: '40px', display: 'block', marginBottom: '12px' },

  errorBanner: {
    background: '#fef2f2', border: '1.5px solid #fecaca',
    borderRadius: '10px', padding: '16px', marginBottom: '12px',
  },
  errorBannerTitle: { fontSize: '14px', fontWeight: '700', color: '#b91c1c', marginBottom: '6px' },
  errorBannerMsg: { fontSize: '13px', color: '#dc2626', lineHeight: '1.5' },

  successBox: { border: '1.5px solid #86efac', background: '#f0fdf4', borderRadius: '10px', padding: '20px' },
  successTitle: { fontSize: '15px', fontWeight: '700', color: '#15803d', marginBottom: '14px' },
  resultRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #dcfce7', fontSize: '13px' },
  resultLabel: { color: '#6b7280' },
  resultVal: { fontWeight: '600', color: '#111827', fontFamily: 'monospace' },
  btnDownload: {
    display: 'inline-block', marginTop: '16px', padding: '10px 18px',
    background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none',
  },
  warnBox: { marginTop: '14px', fontSize: '12px', color: '#92400e', background: '#fffbeb', padding: '10px 12px', borderRadius: '6px' },

  debugCard: { background: '#0f172a', borderRadius: '12px', border: '1px solid #1e293b', overflow: 'hidden' },
  debugToggle: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', padding: '14px 20px',
    background: 'none', border: 'none', borderBottom: '1px solid #1e293b',
    cursor: 'pointer', gap: '12px', textAlign: 'left',
  },
  debugChevron: { fontSize: '10px', color: '#475569', flexShrink: 0 },
  debugEndpoint: { fontSize: '14px', fontWeight: '500', color: '#94a3b8', fontFamily: 'monospace' },
  debugStatus: { fontSize: '12px', fontWeight: '600', flexShrink: 0 },
  debugPanels: { display: 'grid', gridTemplateColumns: '1fr 1fr' },
  debugPanel: { borderRight: '1px solid #1e293b' },
  debugPanelTitle: {
    fontSize: '11px', fontWeight: '700', color: '#475569',
    textTransform: 'uppercase', letterSpacing: '1px',
    padding: '10px 16px', borderBottom: '1px solid #1e293b',
  },
  debugJson: {
    margin: 0, padding: '16px',
    background: '#020617', color: '#7dd3fc',
    fontSize: '12px', lineHeight: '1.6',
    overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
  },
  debugSection: { borderBottom: '1px solid #1e293b' },
  credGrid: { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px 16px' },
  credRow: { display: 'flex', gap: '12px', fontSize: '12px' },
  credKey: { color: '#64748b', minWidth: '160px', fontFamily: 'monospace' },
  credVal: { color: '#e2e8f0', fontFamily: 'monospace', wordBreak: 'break-all' },
};
